import React, { useState } from 'react';
import { confirmDialog } from '../lib/confirm';
import { notifyError, notifySuccess, notifyInfo } from '../lib/notify';
import { Link } from 'react-router-dom';
import axios from '../../config/axios';
import Layout, { TopBar } from '../components/Layout';
import { Button, Meta, Display, Chip, Rule } from '../components/Atoms';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { useApi, formatDate, taskTypeMeta } from '../lib/api';

/**
 * Tasks - lista wszystkich zadań ogrodniczych z filtrami.
 *
 * Endpointy:
 *   GET /api/tasks?limit=500 - lista
 *   POST /api/tasks/:id/complete - oznacz jako wykonane
 *   DELETE /api/tasks/:id
 */

function priorityColor(priority) {
  if (priority === 'high') return 'var(--t-rust)';
  if (priority === 'medium') return 'var(--t-amber)';
  return 'var(--t-text-mute)';
}

function priorityLabel(priority) {
  return { high: 'wysoki', medium: 'średni', low: 'niski' }[priority] || priority;
}

function TaskRow({ task, onComplete, onDelete, onSnooze, onDismiss, today }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const isAuto = !!task.auto_generated;
  const meta = taskTypeMeta(task.task_type);
  const isOverdue = !task.completed && task.due_date && task.due_date < today;
  const isToday = !task.completed && task.due_date === today;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 14,
      padding: '14px 16px', borderRadius: 10,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      borderLeft: `3px solid ${task.completed ? 'var(--t-line-2)' :
                                isOverdue ? 'var(--t-rust)' :
                                isToday ? 'var(--t-sage)' : 'var(--t-line-2)'}`,
      marginBottom: 8,
      opacity: task.completed ? 0.55 : 1,
    }}>
      <button
        type="button"
        onClick={() => !task.completed && onComplete(task.id)}
        disabled={task.completed}
        aria-label={task.completed ? 'Wykonane' : 'Oznacz jako wykonane'}
        style={{
          width: 22, height: 22, marginTop: 2,
          borderRadius: 6,
          background: task.completed ? 'var(--t-sage)' : 'transparent',
          border: `1.5px solid ${task.completed ? 'var(--t-sage)' : 'var(--t-line-2)'}`,
          cursor: task.completed ? 'default' : 'pointer',
          display: 'grid', placeItems: 'center',
          flexShrink: 0,
          color: 'var(--t-bg)', fontSize: 12, lineHeight: 1,
        }}
      >{task.completed ? '✓' : ''}</button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          marginBottom: 4,
        }}>
          <span style={{
            fontSize: 14, color: 'var(--t-text)',
            letterSpacing: '-.01em',
            textDecoration: task.completed ? 'line-through' : 'none',
          }}>{task.title}</span>
          {meta && <Chip tone={meta.tone}>{meta.label}</Chip>}
          {task.priority && task.priority !== 'low' && (
            <Chip tone={task.priority === 'high' ? 'rust' : 'amber'}>
              {priorityLabel(task.priority)}
            </Chip>
          )}
        </div>
        <div style={{
          display: 'flex', gap: 12, fontSize: 11,
          color: 'var(--t-text-mute)',
          fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
        }}>
          {task.due_date && (
            <span style={{
              color: isOverdue ? 'var(--t-rust)' :
                     isToday ? 'var(--t-sage)' : 'var(--t-text-mute)',
            }}>
              {isOverdue ? '⚠ ' : ''}
              {isToday ? 'DZIŚ' : formatDate(task.due_date).toUpperCase()}
            </span>
          )}
          {task.plant_name && <span>· {task.plant_name}</span>}
          {task.recurrence_pattern && <span>· ↻ cykliczne</span>}
        </div>
        {task.description && !task.completed && (
          <div style={{
            fontSize: 12, color: 'var(--t-text-dim)',
            marginTop: 6, lineHeight: 1.45,
          }}>{task.description}</div>
        )}
      </div>

      <div style={{ position: 'relative', flexShrink: 0 }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
          aria-label="Akcje zadania"
          style={{
            background: 'transparent', border: 'none',
            color: 'var(--t-text-mute)', cursor: 'pointer',
            padding: 6, fontSize: 18, lineHeight: 1,
            borderRadius: 6,
          }}
        >⋮</button>
        {menuOpen && (
          <>
            <div
              onClick={() => setMenuOpen(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 10 }}
            />
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', right: 0,
              minWidth: 180, zIndex: 11,
              background: 'var(--t-bg)',
              border: '1px solid var(--t-line)',
              borderRadius: 10,
              boxShadow: '0 12px 32px rgba(0,0,0,.5)',
              padding: 4,
              fontFamily: 'var(--t-sans)', fontSize: 13,
            }}>
              {isAuto && !task.completed && (
                <>
                  <MenuItem label="Odłóż 1 dzień" onClick={() => { setMenuOpen(false); onSnooze?.(task.id, 1); }} />
                  <MenuItem label="Odłóż 3 dni" onClick={() => { setMenuOpen(false); onSnooze?.(task.id, 3); }} />
                  <MenuItem label="Odłóż tydzień" onClick={() => { setMenuOpen(false); onSnooze?.(task.id, 7); }} />
                  <div style={{ height: 1, background: 'var(--t-line)', margin: '4px 0' }} />
                  <MenuItem label="Pomiń to zadanie" onClick={() => { setMenuOpen(false); onDismiss?.(task.id); }} />
                  <div style={{ height: 1, background: 'var(--t-line)', margin: '4px 0' }} />
                </>
              )}
              <MenuItem label="Usuń" danger onClick={() => { setMenuOpen(false); onDelete(task.id); }} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── MenuItem (drobny element z menu kontekstowego) ─────────────
function MenuItem({ label, onClick, danger }) {
  const color = danger ? 'var(--t-rust)' : 'var(--t-text)';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left',
        padding: '8px 12px', borderRadius: 6,
        background: 'transparent', border: 'none',
        color, cursor: 'pointer',
        fontSize: 13, fontFamily: 'var(--t-sans)',
        transition: 'background 150ms ease',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--t-bg-elev)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >{label}</button>
  );
}

export default function Tasks() {
  const [filter, setFilter] = useState('todo'); // 'todo' | 'today' | 'done' | 'all' | 'recurring'
  const [generatingAI, setGeneratingAI] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);
  const { data, loading, error, refetch } = useApi('/api/tasks?limit=500');
  const { data: recurringData, refetch: refetchRecurring } = useApi('/api/tasks/recurring');

  if (error?.status === 401) {
    return <Layout><ErrorState error={error} /></Layout>;
  }
  if (loading) return <Layout><LoadingState /></Layout>;

  const today = new Date().toISOString().split('T')[0];
  const allTasks = data?.data || [];
  const todoTasks = allTasks.filter(t => !t.completed);
  const todayTasks = todoTasks.filter(t => t.due_date === today);
  const overdueTasks = todoTasks.filter(t => t.due_date && t.due_date < today);
  const doneTasks = allTasks.filter(t => t.completed);

  let items = todoTasks;
  if (filter === 'today') items = todayTasks;
  else if (filter === 'done') items = doneTasks;
  else if (filter === 'all') items = allTasks;

  // Sortowanie: spóźnione → dziś → przyszłe → bez daty → wykonane
  items = [...items].sort((a, b) => {
    if (a.completed !== b.completed) return a.completed ? 1 : -1;
    if (!a.due_date && b.due_date) return 1;
    if (a.due_date && !b.due_date) return -1;
    if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
    return 0;
  });

  const handleComplete = async (id) => {
    try {
      await axios.post(`/api/tasks/${id}/complete`);
      refetch();
    } catch (err) {
      notifyError('Nie udało się oznaczyć: ' + (err.response?.data?.error || ''));
    }
  };

  const handleSnooze = async (id, days) => {
    try {
      await axios.post(`/api/tasks/${id}/snooze`, { days });
      refetch();
    } catch (err) {
      notifyError('Nie udało się odłożyć: ' + (err.response?.data?.error || ''));
    }
  };

  const handleDismiss = async (id) => {
    if (!await confirmDialog('Pominąć to zadanie? Nie pojawi się ponownie.')) return;
    try {
      await axios.post(`/api/tasks/${id}/dismiss`);
      refetch();
    } catch (err) {
      notifyError('Nie udało się pominąć: ' + (err.response?.data?.error || ''));
    }
  };

  const handleGenerateAI = async () => {
    if (generatingAI) return;
    setGeneratingAI(true);
    try {
      const res = await axios.post('/api/tasks/generate');
      const count = res.data?.created || res.data?.tasks?.length || 0;
      if (count > 0) {
        notifySuccess(`Wygenerowano ${count} ${count === 1 ? 'zadanie' : count < 5 ? 'zadania' : 'zadań'} z Twojego ogrodu.`);
      } else {
        notifyInfo('Wszystko już zaplanowane — nic nowego do dodania.');
      }
      refetch();
    } catch (err) {
      notifyError('Nie udało się wygenerować: ' + (err.response?.data?.error || ''));
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog('Usunąć zadanie?')) return;
    try {
      await axios.delete(`/api/tasks/${id}`);
      refetch();
    } catch (err) {
      notifyError('Nie udało się usunąć: ' + (err.response?.data?.error || ''));
    }
  };

  const handleDeleteRecurring = async (task) => {
    if (!await confirmDialog(
      `Usuwa szablon cyklicznego zadania "${task.title}" oraz wszystkie przyszłe wystąpienia. Dotychczas wykonane zostają w historii.`,
      { title: 'Usunąć cykliczne zadanie?', confirmLabel: 'Usuń cykl', tone: 'danger' }
    )) return;
    try {
      await axios.delete(`/api/tasks/${task.id}/recurring`);
      notifySuccess('Cykl usunięty.');
      refetchRecurring?.();
      refetch();
    } catch (err) {
      notifyError('Nie udało się usunąć: ' + (err.response?.data?.error || ''));
    }
  };

  const handleSaveRecurring = async (id, payload) => {
    try {
      await axios.put(`/api/tasks/${id}/recurring`, payload);
      notifySuccess('Zaktualizowano cykl.');
      setEditingRecurring(null);
      refetchRecurring?.();
      refetch();
    } catch (err) {
      notifyError('Nie udało się zapisać: ' + (err.response?.data?.error || ''));
    }
  };

  const recurringTasks = Array.isArray(recurringData) ? recurringData : [];

  return (
    <Layout topBar={<TopBar left={<Meta>Zadania</Meta>} />}>
      <div style={{
        maxWidth: 880, margin: '0 auto',
        padding: '40px 32px 60px',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-end', gap: 20, flexWrap: 'wrap',
          marginBottom: 24,
        }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <Meta color="sage" style={{ marginBottom: 10 }}>Lista zadań</Meta>
            <Display size={'clamp(36px, 6vw, 48px)'} style={{
              display: 'block', letterSpacing: '-.03em', lineHeight: 1,
            }}>Co robisz w ogrodzie</Display>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="ghost" onClick={handleGenerateAI} disabled={generatingAI}>
              {generatingAI ? 'Generuję…' : '↻ Auto-generuj z karencji'}
            </Button>
            <Link to="/tasks/new" style={{ textDecoration: 'none' }}>
              <Button variant="primary">+ Nowe zadanie</Button>
            </Link>
          </div>
        </div>

        {overdueTasks.length > 0 && filter !== 'done' && (
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            background: 'rgba(192, 106, 74, 0.08)',
            borderLeft: '3px solid var(--t-rust)',
            marginBottom: 20, fontSize: 13, color: 'var(--t-text)',
          }}>
            ⚠ <strong>{overdueTasks.length}</strong> {overdueTasks.length === 1 ? 'zadanie jest' : 'zadań jest'} spóźnion{overdueTasks.length === 1 ? 'e' : 'ych'}.
          </div>
        )}

        {/* Filtr */}
        <div style={{
          display: 'flex', gap: 6, padding: 4,
          background: 'var(--t-bg-elev)', borderRadius: 10,
          border: '1px solid var(--t-line)',
          marginBottom: 24, width: 'fit-content',
          flexWrap: 'wrap',
        }}>
          {[
            { k: 'todo',      label: `Do zrobienia (${todoTasks.length})` },
            { k: 'today',     label: `Dziś (${todayTasks.length})` },
            { k: 'done',      label: `Wykonane (${doneTasks.length})` },
            { k: 'recurring', label: `↻ Cykliczne (${recurringTasks.length})` },
            { k: 'all',       label: `Wszystkie (${allTasks.length})` },
          ].map(opt => (
            <button
              key={opt.k}
              type="button"
              onClick={() => setFilter(opt.k)}
              style={{
                padding: '8px 14px', borderRadius: 8,
                background: filter === opt.k ? 'var(--t-bg)' : 'transparent',
                border: 'none', cursor: 'pointer',
                color: filter === opt.k ? 'var(--t-text)' : 'var(--t-text-dim)',
                fontFamily: 'var(--t-sans)', fontSize: 13,
                fontWeight: filter === opt.k ? 500 : 400,
              }}
            >{opt.label}</button>
          ))}
        </div>

        {filter === 'recurring' ? (
          recurringTasks.length > 0 ? (
            <div>
              <div style={{
                fontSize: 13, color: 'var(--t-text-dim)',
                marginBottom: 16, lineHeight: 1.55,
                padding: '12px 16px', borderRadius: 10,
                background: 'rgba(168, 196, 158, 0.08)',
                border: '1px solid rgba(168, 196, 158, 0.20)',
              }}>
                Szablony zadań które same się odnawiają w ustawionym rytmie. Kolejne wystąpienia generują się automatycznie po wykonaniu poprzedniego.
              </div>
              {recurringTasks.map(t => (
                <RecurringRow
                  key={t.id}
                  task={t}
                  isEditing={editingRecurring === t.id}
                  onStartEdit={() => setEditingRecurring(t.id)}
                  onCancelEdit={() => setEditingRecurring(null)}
                  onSave={(payload) => handleSaveRecurring(t.id, payload)}
                  onDelete={() => handleDeleteRecurring(t)}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              title="Brak cyklicznych zadań"
              description="Dodaj zadanie z opcją cykliczności (np. 'Podlewanie co 3 dni'), żeby zobaczyć je tutaj."
              action={
                <Link to="/tasks/new" style={{ textDecoration: 'none' }}>
                  <Button variant="primary">+ Dodaj cykliczne zadanie</Button>
                </Link>
              }
            />
          )
        ) : items.length > 0 ? (
          <div>
            {items.map(t => (
              <TaskRow key={t.id} task={t}
                onComplete={handleComplete}
                onDelete={handleDelete}
                onSnooze={handleSnooze}
                onDismiss={handleDismiss}
                today={today} />
            ))}
          </div>
        ) : (
          <EmptyState
            title={
              filter === 'today' ? 'Nic na dziś' :
              filter === 'done' ? 'Brak wykonanych' :
              'Brak zadań'
            }
            description={
              filter === 'todo' ? 'Wszystko zrobione albo nic nie zaplanowano. Dodaj nowe zadanie nad listą.' :
              filter === 'today' ? 'Możesz odpocząć — żadne zadanie nie ma terminu na dziś.' :
              filter === 'done' ? 'Tutaj będą zadania, które oznaczysz jako wykonane.' :
              'Dodaj pierwsze zadanie żeby zacząć.'
            }
            action={
              <Link to="/tasks/new" style={{ textDecoration: 'none' }}>
                <Button variant="primary">+ Dodaj zadanie</Button>
              </Link>
            }
          />
        )}
      </div>
    </Layout>
  );
}

// ── RecurringRow — wiersz cyklicznego zadania z edycją inline ──────
function RecurringRow({ task, isEditing, onStartEdit, onCancelEdit, onSave, onDelete }) {
  const [freq, setFreq] = useState(task.recurrence_frequency || 7);
  const [endDate, setEndDate] = useState(task.recurrence_end_date || '');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (isEditing) {
      setFreq(task.recurrence_frequency || 7);
      setEndDate(task.recurrence_end_date || '');
    }
  }, [isEditing, task]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    setSaving(true);
    try {
      await onSave({
        recurrence_frequency: Number(freq),
        recurrence_end_date: endDate || null,
      });
    } finally {
      setSaving(false);
    }
  };

  const meta = taskTypeMeta(task.task_type);

  return (
    <div style={{
      padding: '14px 16px', borderRadius: 10,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      borderLeft: '3px solid var(--t-sage)',
      marginBottom: 10,
    }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 14,
        flexWrap: 'wrap',
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: 'rgba(168, 196, 158, 0.12)',
          display: 'grid', placeItems: 'center',
          color: 'var(--t-sage)', flexShrink: 0,
          fontSize: 14,
        }}>↻</div>

        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            marginBottom: 4,
          }}>
            <span style={{
              fontSize: 14, color: 'var(--t-text)',
              letterSpacing: '-.01em', fontWeight: 500,
            }}>{task.title}</span>
            {meta && <Chip tone={meta.tone}>{meta.label}</Chip>}
          </div>
          <div style={{
            fontSize: 11, color: 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            display: 'flex', gap: 12, flexWrap: 'wrap',
          }}>
            <span>CO {task.recurrence_frequency} {task.recurrence_frequency === 1 ? 'DZIEŃ' : 'DNI'}</span>
            {task.next_occurrence && (
              <span style={{ color: 'var(--t-sage)' }}>
                NASTĘPNE: {formatDate(task.next_occurrence).toUpperCase()}
              </span>
            )}
            {task.recurrence_end_date && (
              <span style={{ color: 'var(--t-amber)' }}>
                DO: {formatDate(task.recurrence_end_date).toUpperCase()}
              </span>
            )}
            {task.plant_name && <span>· {task.plant_name}</span>}
          </div>
          {task.description && !isEditing && (
            <div style={{
              fontSize: 12, color: 'var(--t-text-dim)',
              marginTop: 6, lineHeight: 1.45,
            }}>{task.description}</div>
          )}
        </div>

        {!isEditing && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={onStartEdit} style={recBtn('ghost')}>Edytuj</button>
            <button type="button" onClick={onDelete} style={recBtn('danger')}>Usuń</button>
          </div>
        )}
      </div>

      {isEditing && (
        <form onSubmit={handleSubmit} style={{
          marginTop: 14, paddingTop: 14,
          borderTop: '1px solid var(--t-line)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12, alignItems: 'flex-end',
        }}>
          <div>
            <div style={recLabel}>Co ile dni</div>
            <input
              type="number"
              aria-label="Co ile dni"
              min="1" max="365"
              value={freq}
              onChange={e => setFreq(e.target.value)}
              required
              style={recInput}
            />
          </div>
          <div>
            <div style={recLabel}>Koniec cyklu (opcjonalne)</div>
            <input
              type="date"
              aria-label="Koniec cyklu"
              value={endDate || ''}
              onChange={e => setEndDate(e.target.value)}
              style={recInput}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onCancelEdit} style={recBtn('ghost')}>Anuluj</button>
            <button type="submit" disabled={saving} style={recBtn('primary')}>
              {saving ? 'Zapisuję…' : 'Zapisz'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

const recLabel = {
  fontFamily: 'var(--t-mono)', fontSize: 10,
  letterSpacing: '.18em', textTransform: 'uppercase',
  color: 'var(--t-text-mute)',
  marginBottom: 6,
};

const recInput = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  background: 'var(--t-bg)', color: 'var(--t-text)',
  border: '1px solid var(--t-line-2)',
  fontFamily: 'var(--t-sans)', fontSize: 13,
  outline: 'none', boxSizing: 'border-box',
};

const recBtn = (variant) => {
  const base = {
    padding: '7px 12px', borderRadius: 8,
    fontFamily: 'var(--t-sans)', fontSize: 12,
    fontWeight: 500, cursor: 'pointer',
    letterSpacing: '-.01em',
  };
  if (variant === 'primary') {
    return { ...base, background: 'var(--t-sage)', border: 'none', color: 'var(--t-bg)' };
  }
  if (variant === 'danger') {
    return { ...base, background: 'transparent', color: 'var(--t-rust)', border: '1px solid var(--t-rust)' };
  }
  return { ...base, background: 'transparent', color: 'var(--t-text-dim)', border: '1px solid var(--t-line-2)' };
};
